import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { db, doc, getDoc } from '../../firebase';

const applyBackground = (backgroundData) => {
    const appElement = document.querySelector('.App');
    if (!appElement) return;

    const { url, opacity } = backgroundData || {};

    if (url) {
        appElement.style.setProperty('--custom-background-image', `url("${url}")`);
        const overlayOpacity = 1 - (opacity || 0.3);
        appElement.style.setProperty('--background-overlay-opacity', overlayOpacity);
        appElement.classList.add('custom-background');
    } else {
        appElement.style.removeProperty('--custom-background-image');
        appElement.style.removeProperty('--background-overlay-opacity');
        appElement.classList.remove('custom-background');
    }
};

function ThemeLoader() {
    const location = useLocation();
    // Refs so the (once-only) background listener can read the current path, and
    // the path-change effect can re-apply the latest background, without either
    // one re-subscribing the Firestore listener.
    const pathnameRef = useRef(location.pathname);
    const bgDataRef = useRef(null);

    // Re-apply background on navigation (hidden on homepage) — no re-subscribe.
    useEffect(() => {
        pathnameRef.current = location.pathname;
        applyBackground(location.pathname === '/' ? null : bgDataRef.current);
    }, [location.pathname]);

    // NOTE: the theme colours are applied by App.js's `settings/globalTheme` listener
    // (it sets every --gaming-* CSS variable from the theme name). The old
    // `settings/activeTheme` listener here injected the SAME colours via a stylesheet
    // that App's inline styles override anyway, so it was a redundant duplicate read on
    // every visit and has been removed. Only the background listener remains.

    // Background — one-time cached read (was a live listener per visitor for a
    // rarely-changing settings doc). localStorage serves instantly and, while fresh,
    // skips the Firestore read entirely; refresh at most once per TTL window.
    useEffect(() => {
        const LS_KEY = 'fl_active_bg_v1';
        const TTL = 30 * 60 * 1000; // 30 minutes
        let fresh = false;
        try {
            const raw = localStorage.getItem(LS_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                bgDataRef.current = parsed.data;
                applyBackground(pathnameRef.current === '/' ? null : bgDataRef.current);
                fresh = (Date.now() - parsed.ts) < TTL;
            }
        } catch (_) { }
        if (fresh) return; // cache still fresh — no Firestore read
        getDoc(doc(db, "settings", "activeBackground")).then((docSnap) => {
            bgDataRef.current = docSnap.exists() ? docSnap.data() : null;
            try { localStorage.setItem(LS_KEY, JSON.stringify({ data: bgDataRef.current, ts: Date.now() })); } catch (_) { }
            applyBackground(pathnameRef.current === '/' ? null : bgDataRef.current);
        }).catch((error) => {
            console.error("Error fetching active background:", error);
        });
    }, []);

    return null;
}

export default ThemeLoader;
